import React, { useState } from 'react';
import { usePlayerPrograms } from '@/hooks/usePlayerPrograms';
import { useProgramManagement } from '@/hooks/useProgramManagement';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Plus, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';

interface PlayerProgramAssignmentsProps {
  playerId: string;
  teamId?: string;
}

export const PlayerProgramAssignments = ({ playerId, teamId }: PlayerProgramAssignmentsProps) => {
  const { playerPrograms, loading, assignProgram, removeAssignment, updateAssignmentStatus } = usePlayerPrograms(playerId);
  const { programs, loading: programsLoading } = useProgramManagement(teamId);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Filter available programs (not already assigned)
  const availablePrograms = programs.filter(program => 
    program.is_active && !playerPrograms.some(pp => pp.program_id === program.id)
  );

  const handleAssignProgram = async () => {
    if (!selectedProgramId) {
      toast.error('Please select a program');
      return;
    }

    setIsAssigning(true);
    try {
      const success = await assignProgram(playerId, selectedProgramId);
      if (success) {
        setSelectedProgramId('');
      }
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveAssignment = async (playerProgramId: string) => {
    await removeAssignment(playerProgramId);
  };

  const handleStatusChange = async (playerProgramId: string, newStatus: string) => {
    await updateAssignmentStatus(playerProgramId, newStatus);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading || programsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2">Loading programs...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Assign New Program */}
      {availablePrograms.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Assign Program
            </CardTitle>
            <CardDescription>
              Assign this player to a training program
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="text-sm font-medium">Select Program</label>
                <Select value={selectedProgramId} onValueChange={setSelectedProgramId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a program..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePrograms.map((program) => (
                      <SelectItem key={program.id} value={program.id}>
                        {program.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={handleAssignProgram} 
                disabled={!selectedProgramId || isAssigning}
              >
                {isAssigning ? 'Assigning...' : 'Assign Program'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Assignments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Program Assignments ({playerPrograms.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {playerPrograms.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Programs Assigned</h3>
              <p className="text-muted-foreground">
                This player is not currently assigned to any training programs.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {playerPrograms.map((assignment) => (
                <div key={assignment.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-medium">{assignment.program?.title}</h4>
                      <Badge className={getStatusColor(assignment.status)}>
                        {assignment.status}
                      </Badge>
                    </div>
                    {assignment.program?.description && (
                      <p className="text-sm text-muted-foreground mb-2">
                        {assignment.program.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Assigned: {new Date(assignment.assigned_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      value={assignment.status}
                      onValueChange={(value) => handleStatusChange(assignment.id, value)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveAssignment(assignment.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};